import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar,
  Users,
  ChevronLeft,
  Share2,
  User,
  Mail,
  Loader2,
  DollarSign,
  CalendarDays,
  Clock3,
  Building,
  GraduationCap,
  AlertCircle,
  LinkIcon,
  X,
  MapPinned,
  UserCheck,
  Video,
  Ticket,
  PartyPopper,
  Info,
  BookOpen,
  Edit,
} from "lucide-react";
import RegistrationModal from "../components/RegistrationModal";



const EventMetric = ({ icon: Icon, label, value, className }) => (
  <div className={`flex items-center space-x-2 ${className}`}>
    <Icon className="h-5 w-5 text-gray-400" />
    <div>
      <div className="text-sm font-medium text-gray-900">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  </div>
);

const EventDetails = () => {
  // Move all useState declarations to the top
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentAlumni, setCurrentAlumni] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);

  // Move useEffect to be right after all useState declarations
  useEffect(() => {
    const initialize = async () => {
      await Promise.all([fetchEventDetails(), fetchCurrentAlumni()]);
    };
    initialize();
  }, [id]);

  // Define all the helper functions after hooks
  const fetchCurrentAlumni = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/alumni/me`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch alumni details");
      const data = await response.json();
      setCurrentAlumni(data);
    } catch (err) {
      console.error("Error fetching alumni details:", err);
    }
  };

  const fetchEventDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/events/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch event details");
      }

      const data = await response.json();
      setEvent(data);
      console.log("data", data);
    } catch (err) {
      console.error("Error in fetchEventDetails:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load the Razorpay SDK from the CDN
  function loadScript(src) {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  }

  const handleRegistrationSubmit = async (formData) => {
    console.log("Processing registration...");
    setIsRegistering(true);
    try {
      const token = localStorage.getItem("token");

      if (event.registration.fee.amount === 0) {
        // For free events, directly register without payment
        setIsRegistering(true);
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/events/${id}/payment`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              ...formData,
              eventId: id,
              registrationType: "free",
              eventTitle: event.title,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Registration failed");
        }

        await fetchEventDetails();
        setShowRegistrationModal(false);
        setIsRegistering(false);
        alert("Registration successful! Check your email for confirmation.");
        return;
      }

      // For paid events, proceed with payment flow
      const res = await loadScript(
        "https://checkout.razorpay.com/v1/checkout.js"
      );

      if (!res) {
        console.log(
          "Payment gateway failed to load. Please check your internet connection."
        );
        return;
      }

      // Get payment order from backend
      const orderResponse = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/events/${id}/payment`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...formData,
            eventId: id,
            registrationType: "paid",
            eventTitle: event.title,
          }),
        }
      );

      if (!orderResponse.ok) {
        throw new Error("Failed to initialize payment");
      }

      const orderData = await orderResponse.json();

      // Configure Razorpay
      const options = {
        key: "rzp_test_LkIEjsDE6zd6Cx",
        currency: orderData.data.currency,
        amount: orderData.data.amount,
        order_id: orderData?.data?.id,
        name: "Alumni Event Registration",
        description: `Registration for ${event.title}`,
        image: "https://i.ibb.co/JB1mSr9/SAARTHI-removebg-preview.png",
        handler: async function (response) {
          // Verify payment with backend
          console.log("Payment response:", response);

          try {
            // Show verifying payment loader
            setIsRegistering(true);
            const verifyResponse = await fetch(
              `${process.env.REACT_APP_BASE_URL}/api/events/${id}/verify-payment`,
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  razorpay_order_id: response.razorpay_order_id
                }),
              }
            );

            console.log("verifyResponse", verifyResponse);

            if (verifyResponse.ok) {
              console.log("Payment successful! You are now registered for the event.");
              await fetchEventDetails();
              setShowRegistrationModal(false);
              alert("Payment successful! You are now registered for the event.");
            } else {
              const errorData = await verifyResponse.json();
              throw new Error(errorData.message || "Payment verification failed");
            }
          } catch (error) {
            console.error("Verification error:", error);
            alert("Payment verification failed. Please contact support.");
          } finally {
            setIsRegistering(false);
          }
        },
        modal: {
          ondismiss: function() {
            console.log("Payment modal closed");
            setIsRegistering(false);
          }
        },
        theme: {
          color: "#3B82F6"
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();

      paymentObject.on("payment.failed", function (response) {
        console.log("Payment failed. Please try again.");
        console.error("Payment failed:", response.error);
        setIsRegistering(false);
      });
    } catch (error) {
      console.error("Payment error:", error);
      console.log("Payment failed. Please try again later.");
      setIsRegistering(false);
    }
  };



  const isUserRegistered = () => {
    if (!event?.registrations || !currentAlumni?._id) {
      console.log('Missing event registrations or currentAlumni:', {
        registrations: event?.registrations,
        currentAlumniId: currentAlumni?._id
      });
      return false;
    }
    
    // Check registrations array for the current alumni's registration
    const isRegistered = event.registrations.some(registration => {
      const alumniId = registration.alumni?._id || registration.alumni;
      const registrationMatch = alumniId === currentAlumni._id || 
                              alumniId?.toString() === currentAlumni._id?.toString();
                              
      console.log('Registration check:', {
        registrationAlumniId: alumniId,
        currentAlumniId: currentAlumni._id,
        isMatch: registrationMatch
      });
      
      return registrationMatch;
    });
  
    return isRegistered;
  };
  
  const getRegistrationStatus = () => {
    if (!event || !currentAlumni) return {
      canRegister: false,
      message: "Loading...",
      buttonStyle: "bg-gray-400 cursor-not-allowed"
    };
  
    const now = new Date();
  const deadline = new Date(event.registration.deadline);
  const isDeadlinePassed = now > deadline;
  const isFull = event.capacity.max && event.capacity.remaining <= 0;
  
  // Use the isRegistered property from the API response
  const registered = event.isRegistered || isUserRegistered();

  console.log('Registration Status Check:', {
    currentAlumniId: currentAlumni._id,
    eventIsRegistered: event.isRegistered,
    localIsRegistered: isUserRegistered(),
    registrations: event.registrations,
    registered
  });

  if (registered) {
    return {
      canRegister: false,
      message: "Already Registered",
      buttonStyle: "bg-green-600 cursor-default hover:bg-green-600"
    };
  }
  
    if (isDeadlinePassed) {
      return {
        canRegister: false,
        message: "Registration Closed",
        buttonStyle: "bg-gray-500 cursor-not-allowed"
      };
    }
  
    if (isFull) {
      return {
        canRegister: false,
        message: "Event Full",
        buttonStyle: "bg-red-500 cursor-not-allowed"
      };
    }
  
    return {
      canRegister: true,
      message: "Register Now",
      buttonStyle: "bg-blue-600 hover:bg-blue-700"
    };
  };
  

 
 


  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: event.title,
        text: `Join us for ${event.title}`,
        url: window.location.href,
      });
    } else {
      setShowShareMenu(!showShareMenu);
    }
  };

  const formatDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    if (startDate.toDateString() === endDate.toDateString()) {
      return {
        date: startDate.toLocaleDateString("en-US", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        }),
        time: `${startDate.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        })} - ${endDate.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        })}`,
      };
    }

    return {
      date: `${startDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
      })} - ${endDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })}`,
      time: "Multiple Days",
    };
  };

  const getDaysUntilEvent = () => {
    const now = new Date();
    const start = new Date(event?.schedule?.startDate);
    const diffTime = start - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getEventStatus = () => {
    const now = new Date();
    const start = new Date(event?.schedule?.startDate);
    const end = new Date(event?.schedule?.endDate);

    if (now < start)
      return {
        text: "Upcoming",
        color: "bg-blue-100 text-blue-800",
      };
    if (now > end)
      return {
        text: "Completed",
        color: "bg-gray-100 text-gray-800",
      };
    return {
      text: "In Progress",
      color: "bg-green-100 text-green-800",
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <p className="text-gray-600 animate-pulse">
            Loading event details...
          </p>
        </div>
      </div>
    );
  }
  const renderRegistrationButton = () => {
    const registrationStatus = getRegistrationStatus();
    const buttonProps = {
      onClick: () => registrationStatus.canRegister && setShowRegistrationModal(true),
      className: `mt-6 w-full inline-flex items-center justify-center px-4 py-2 
        border border-transparent rounded-lg shadow-sm text-sm font-medium text-white 
        transition-colors duration-200 ${registrationStatus.buttonStyle}`,
      disabled: !registrationStatus.canRegister
    };
    
    return (
      <button {...buttonProps}>
        <Ticket className="h-4 w-4 mr-2" />
        {registrationStatus.message}
      </button>
    );
  };
  const dateRange = formatDateRange(
    event?.schedule?.startDate,
    event?.schedule?.endDate
  );
  const daysUntil = getDaysUntilEvent();
  const status = getEventStatus();
  const isOrganizer = event?.organizer?.organizedBy?._id === currentAlumni?._id;
  const isRegistered = () => {
    if (!event?.registrations || !currentAlumni) return false;
    return event.registrations.some(reg => {
      const alumniId = reg.alumni?._id || reg.alumni;
      return alumniId && (alumniId === currentAlumni._id || alumniId.toString() === currentAlumni._id.toString());
    });
  };
 

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error || "Event not found"}</p>
          <button
            onClick={() => navigate("/events")}
            className="text-blue-500 hover:text-blue-700"
          >
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  const isUpcoming = new Date(event.schedule.startDate) > new Date();


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Image */}
      <div className="relative h-96 bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 overflow-hidden">
        {event.images?.[0] && (
          <>
            <div className="absolute inset-0">
              <img
                src={event.images[0]}
                alt={event.title}
                className="w-full h-full object-cover opacity-20"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent" />
            </div>
          </>
        )}

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <button
            onClick={() => navigate("/events")}
            className="absolute top-8 text-white hover:text-gray-200 flex items-center"
          >
            <ChevronLeft className="h-5 w-5 mr-1" />
            Back to Events
          </button>
          <div className="absolute top-8 right-4 flex items-center space-x-4">
            {isOrganizer && (
              <button
                onClick={() => navigate(`/events/edit/${id}`)}
                className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 backdrop-blur-lg 
        rounded-lg text-white hover:bg-opacity-30 transition-colors"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Event
              </button>
            )}
            <button
              onClick={handleShare}
              className="p-2 text-white hover:bg-white/20 rounded-full transition-colors"
            >
              <Share2 className="h-5 w-5" />
            </button>
          </div>

          <div className="pt-32 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-4"
            >
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${status.color}`}
              >
                {status.text}
              </span>
              <h1 className="text-4xl font-bold text-white sm:text-5xl">
                {event.title}
              </h1>
             
            </motion.div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Event Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Info Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <EventMetric
                  icon={CalendarDays}
                  label="Date"
                  value={dateRange.date}
                />
                <EventMetric
                  icon={Clock3}
                  label="Time"
                  value={dateRange.time}
                />
                <EventMetric
                  icon={Users}
                  label="Capacity"
                  value={`${event.registrations.length}/${
                    event.capacity.max || "∞"
                  }`}
                />
                <EventMetric
                  icon={DollarSign}
                  label="Registration Fee"
                  value={
                    event.registration.fee.amount > 0
                      ? `${event.registration.fee.amount} ${event.registration.fee.currency}`
                      : "Free"
                  }
                />
              </div>
            </motion.div>

            {/* Event Description */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                About This Event
              </h2>
              <div className="prose max-w-none text-gray-600">
                {event.description}
              </div>
            </div>

            {/* Agenda Section */}
            {event.agenda?.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  Event Schedule
                </h2>
                <div className="space-y-6">
                  {event.agenda.map((item, index) => (
                    <div key={index} className="relative pl-8 pb-6 last:pb-0">
                      <div className="absolute left-0 top-0 h-full w-px bg-blue-200">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-4 w-4 rounded-full border-2 border-blue-500 bg-white" />
                      </div>
                      <div>
                        <time className="text-sm font-medium text-blue-600">
                          {new Date(item.time).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </time>
                        <h3 className="mt-1 text-lg font-medium text-gray-900">
                          {item.title}
                        </h3>
                        {item.description && (
                          <p className="mt-1 text-gray-600">
                            {item.description}
                          </p>
                        )}
                        {item.speaker && (
                          <div className="mt-2 flex items-center text-sm text-gray-500">
                            <User className="h-4 w-4 mr-1.5" />
                            <span>Speaker: {item.speaker}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Location/Venue Details */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {event.venue.isVirtual
                  ? "Virtual Event Details"
                  : "Event Venue"}
              </h2>

              {event.venue.isVirtual ? (
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Video className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Virtual Event
                    </h3>
                    {event.venue.meetingLink && (
                      <a
                        href={event.venue.meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center text-blue-600 hover:text-blue-800"
                      >
                        <LinkIcon className="h-4 w-4 mr-1.5" />
                        Join Meeting
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <MapPinned className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {event.venue.name}
                      </h3>
                      <p className="text-gray-600">{event.venue.address}</p>
                      <p className="text-gray-600">
                        {event.venue.city}, {event.venue.state}{" "}
                        {event.venue.pincode}
                      </p>
                    </div>
                  </div>

                  {/* Add a map here if you have coordinates */}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Registration Card */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Registration Details
              </h3>

   
               <div className="space-y-4">
       {/* Registration Status */}
       <div className="flex justify-between items-center pb-4 border-b">
          <span className="text-gray-600">Status</span>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
            ${event.registration.isRequired ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"}`}
          >
            {event.registration.isRequired ? "Registration Required" : "Open Entry"}
          </span>
        </div>

        {/* Deadline */}
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Registration Closes</span>
          <span className="font-medium text-gray-900">
            {new Date(event.registration.deadline).toLocaleDateString()}
          </span>
        </div>

        {/* Available Spots */}
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Available Spots</span>
          <span className="font-medium text-gray-900">
            {event.capacity.remaining}/{event.capacity.max || "∞"}
          </span>
        </div>
      </div>

              {/* Registration Button */}
              {renderRegistrationButton()}
            </div>

            {/* Event Highlights */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Event Highlights
              </h3>
              {/* Continue from Event Highlights section */}
              <div className="space-y-3">
                <div className="flex items-center space-x-3 text-gray-600">
                  <PartyPopper className="h-5 w-5 text-yellow-500" />
                  <span>Hands-on workshop experience</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-600">
                  <BookOpen className="h-5 w-5 text-blue-500" />
                  <span>Expert-led sessions</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-600">
                  <UserCheck className="h-5 w-5 text-green-500" />
                  <span>Networking opportunities</span>
                </div>
              </div>
            </div>
          </div>

          {/* Timer Card (for upcoming events) */}
          {daysUntil > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Event Starts In
              </h3>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-2xl font-bold text-blue-600">
                    {Math.floor(daysUntil)}
                  </div>
                  <div className="text-xs text-gray-500">Days</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-2xl font-bold text-blue-600">
                    {Math.floor((daysUntil % 1) * 24)}
                  </div>
                  <div className="text-xs text-gray-500">Hours</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-2xl font-bold text-blue-600">
                    {Math.floor((((daysUntil % 1) * 24) % 1) * 60)}
                  </div>
                  <div className="text-xs text-gray-500">Minutes</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-2xl font-bold text-blue-600">
                    {Math.floor((((((daysUntil % 1) * 24) % 1) * 60) % 1) * 60)}
                  </div>
                  <div className="text-xs text-gray-500">Seconds</div>
                </div>
              </div>
            </div>
          )}

          {/* Organizer Info */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Organized By
            </h3>
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                {event.organizer.type === "College" ? (
                  <Building className="h-6 w-6 text-gray-600" />
                ) : (
                  <GraduationCap className="h-6 w-6 text-gray-600" />
                )}
              </div>
              <div>
                <h4 className="text-base font-medium text-gray-900">
                  {event.organizer.organizedBy.name}
                </h4>
                <p className="text-sm text-gray-500">
                  {event.organizer.type === "College"
                    ? "Educational Institution"
                    : "Alumni Association"}
                </p>
              </div>
            </div>
          </div>

          {/* Share Card */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Share Event</h3>
              <button
                onClick={handleShare}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <Share2 className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() =>
                  window.open(
                    `mailto:?subject=${encodeURIComponent(
                      event.title
                    )}&body=${encodeURIComponent(window.location.href)}`
                  )
                }
                className="flex-1 flex items-center justify-center px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                <Mail className="h-4 w-4 mr-2" />
                Email
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  // Add a toast notification here
                }}
                className="flex-1 flex items-center justify-center px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                <LinkIcon className="h-4 w-4 mr-2" />
                Copy Link
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Images Gallery */}
      {event.images?.length > 1 && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Event Gallery
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {event.images.map((image, index) => (
              <div
                key={index}
                className="relative aspect-video rounded-lg overflow-hidden"
              >
                <img
                  src={image}
                  alt={`${event.title} - Image ${index + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Links & Info */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gray-50 rounded-xl p-6">
          <div className="flex items-center space-x-3 text-gray-600">
            <Info className="h-5 w-5" />
            <span>Need assistance? Contact the organizer for support.</span>
          </div>
        </div>
        <div className="bg-gray-50 rounded-xl p-6">
          <div className="flex items-center space-x-3 text-gray-600">
            <AlertCircle className="h-5 w-5" />
            <span>Review event guidelines before attending.</span>
          </div>
        </div>
        <div className="bg-gray-50 rounded-xl p-6">
          <div className="flex items-center space-x-3 text-gray-600">
            <Calendar className="h-5 w-5" />
            <span>Add to your calendar for reminders.</span>
          </div>
        </div>
      </div>

      {/* Share Menu Modal */}
      {showShareMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full mx-4"
          >
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Share Event
            </h3>
            <div className="space-y-4">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  setShowShareMenu(false);
                }}
                className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <LinkIcon className="h-4 w-4 mr-2" />
                Copy Link
              </button>
              <button
                onClick={() => {
                  window.open(
                    `mailto:?subject=${event.title}&body=Check out this event: ${window.location.href}`
                  );
                  setShowShareMenu(false);
                }}
                className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Mail className="h-4 w-4 mr-2" />
                Share via Email
              </button>
            </div>
            <button
              onClick={() => setShowShareMenu(false)}
              className="mt-4 w-full px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Cancel
            </button>
          </motion.div>
        </div>
      )}
      <RegistrationModal
        isOpen={showRegistrationModal}
        onClose={() => setShowRegistrationModal(false)}
        onSubmit={handleRegistrationSubmit}
        isLoading={isRegistering}
      />
    </div>
  );
};

export default EventDetails;
